import { Fragment, HTMLAttributes, useContext, useEffect, useMemo, useRef } from "react";
import { Alert, Breadcrumbs, Box, IconButton, Popover, FormControl, InputLabel, MenuItem } from "@mui/material";
import { displayValidationStatus, FormSection, parseSafeInt } from "Backend";
import Flex from "components/Flex";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { fetchForm, selectForm, selectFormFields, selectFormPages, selectFormSections } from "redux/slices/form.slice";
import { P } from "theme/typography";
import { selectCaseInfo, selectSectionValues } from "redux/slices/case";
import { CaseFormContext } from "contexts/CaseFormContext";
import { BroadcastChannelContext } from "contexts/BroadcastChannelContext";
import { useSnackbar } from "notistack";
import { Case, OutcomeStatus, SectionValueView, ValidationStatus } from "generated/graphql";
import { SimpleHoverLinkSX } from "theme";
import { Link } from "react-router-dom";
import {
  selectClientLens,
  selectClientLensSectionGroups,
  selectLensOutcomeStatusConfiguration,
} from "redux/slices/clientLens";
import useSearchParamsWithUpdate from "helpers/hooks/useSearchParamsWithUpdate";
import LoadingPattern from "components/LoadingPattern";
import { LensRefreshAlertContext } from "contexts/LensRefreshAlertContext";
import { useGroupLocations } from "routes/lens-editor/custom-properties/useGroupLocations";
import SBRCaseOutline from "./CaseOutline";
import { CaseDisplayGroups } from "routes/lenses/lens/case/CaseDisplayGroups";
import FormFieldProvider from "routes/lenses/lens/case/FormFieldProvider";
import { SBRSectionStatus } from "./SectionOutcome";
import { History } from "@mui/icons-material";
import { usePopover } from "helpers/hooks/usePopover";
import { SectionHistory } from "./SectionHistory";
import { HeaderCard, HeaderCardTitle } from "components/HeaderCard";
import { SBRMemoFormFieldEditor } from "./FormFieldEditor";
import { cn } from "components/lib/utils";
import { SectionStatusPicker } from "./SectionStatusPicker";
import { useSectionValue } from "./useSectionValue";
import PSelect from "components/PSelect";
import { AssigneeChipWithPopover } from "./AssigneeChip";
import { SectionValueAssignment } from "./SectionValueAssignment";

function SectionTitle({
  section,
  formPageId,
  caseId,
  formId,
  sectionValue,
}: {
  section: FormSection;
  formPageId?: number;
  caseId: number;
  formId: number;
  sectionValue?: SectionValueView;
} & HTMLAttributes<HTMLDivElement>) {
  const popover = usePopover();
  const sectionValueHook = useSectionValue({ caseId, formId });
  const validationStatus = sectionValue?.validationStatus ?? ValidationStatus.Unvalidated;
  const validationStatuses = Object.values(ValidationStatus);
  const { sbrEnabled } = useAppSelector(selectLensOutcomeStatusConfiguration);

  return (
    <>
      <div className="flex gap-2 items-center">
        {sectionValue !== undefined && (
          <SectionStatusPicker
            reviewState={sectionValue.reviewState}
            outcomeEnabled={section.outcomeEnabled}
            validationEnabled={section.validationEnabled}
            onChange={(rs) => sectionValueHook.updateReviewState([{ sectionId: section.id, setReviewState: rs }])}
          />
        )}
        <Link to={`?formPageId=${formPageId}&sectionId=${section.id}`}>
          <p className="text-sm font-semibold cursor-pointer text-ellipsis hover:underline">{section.name}</p>
        </Link>
        <IconButton onClick={(evt) => popover.onPopoverOpen(evt.currentTarget)}>
          <History />
        </IconButton>
        <Popover
          open={popover.isOpen}
          anchorEl={popover.anchorEl}
          onClose={popover.onPopoverClose}
          sx={{ maxWidth: 1200 }}>
          {popover.isOpen && <SectionHistory caseId={caseId} sectionId={section.id} formId={formId} />}
        </Popover>
        <div className="grow" />
        {/* Width of longest option to prevent movement when changing options */}
        {section.validationEnabled && (
          <FormControl className="w-[165px] mr-6">
            <InputLabel>QA Outcome</InputLabel>
            <PSelect
              label="QA Outcome"
              slotProps={{
                input: {
                  className:
                    validationStatus === ValidationStatus.Issue
                      ? "tracking-wider text-red-500"
                      : "tracking-wider text-gray-500",
                },
              }}
              value={validationStatus}
              onChange={(evt) =>
                sectionValueHook.updateValidationStatus(section.id, evt.target.value as ValidationStatus)
              }>
              {validationStatuses.map((status) => (
                <MenuItem className="tracking-wider" key={status} value={status}>
                  {displayValidationStatus(status, sbrEnabled).toUpperCase()}
                </MenuItem>
              ))}
            </PSelect>
          </FormControl>
        )}

        {sectionValue !== undefined && (
          <SectionValueAssignment sectionValue={sectionValue}>
            {({ userIsQAAssignee, userIsReviewAssignee, outcomeAssignee, qaAssignee }) => (
              <>
                {section.outcomeEnabled && (
                  <div className="flex flex-col items-center gap-2">
                    <AssigneeChipWithPopover
                      formId={formId}
                      user={outcomeAssignee}
                      isMe={userIsReviewAssignee}
                      isQa={false}
                      caseId={caseId}
                      sectionId={section.id}
                    />
                    <p className="text-xxs">Review</p>
                  </div>
                )}
                {section.validationEnabled && (
                  <div className="flex flex-col items-center gap-2">
                    <AssigneeChipWithPopover
                      formId={formId}
                      user={qaAssignee}
                      isMe={userIsQAAssignee}
                      isQa
                      caseId={caseId}
                      sectionId={section.id}
                    />
                    <p className="text-xxs">QA</p>
                  </div>
                )}
              </>
            )}
          </SectionValueAssignment>
        )}
      </div>
    </>
  );
}

function SectionSummary({
  section,
  sectionValue,
  caseId,
  formId,
}: {
  section: FormSection;
  sectionValue?: SectionValueView;
  caseId: number;
  formId: number;
}) {
  return (
    <>
      {section.outcomeEnabled && (
        <SBRSectionStatus caseId={caseId} formId={formId} section={section} sectionValue={sectionValue} />
      )}
    </>
  );
}

function SectionCaseDisplayGroups({ section }: { section: FormSection }) {
  const sectionDisplayGroups = useAppSelector(selectClientLensSectionGroups);
  const groups = sectionDisplayGroups.filter((group) => group.locations.find((l) => l.sectionId === section.id));
  const groupLocations = useGroupLocations(groups).sections[section.id] ?? [];

  return (
    <>
      {groupLocations.length > 0 && (
        <CaseDisplayGroups groups={groupLocations.map((gl) => gl.group)} sx={{ border: 0, flexShrink: 0, p: 0 }} />
      )}
    </>
  );
}

export interface CaseFormProps {
  lensId: number;
  caseId: number;
  lensCase?: Case;
}

const SBRCaseForm = (props: CaseFormProps) => {
  const { caseId } = props;
  const caseInfo = useAppSelector(selectCaseInfo);
  const form = useAppSelector(selectForm);
  const formFields = useAppSelector(selectFormFields);
  const formSections = useAppSelector(selectFormSections);
  const formPages = useAppSelector(selectFormPages);
  const sectionValues = useAppSelector(selectSectionValues);
  const { searchParams } = useSearchParamsWithUpdate();
  const sectionId = parseSafeInt(searchParams.get("sectionId"));
  const stringFormPageId = searchParams.get("formPageId");
  const formPageId = stringFormPageId !== null && stringFormPageId !== "" ? +stringFormPageId : formPages[0]?.id;
  const caseForm = useContext(CaseFormContext);
  const { enqueueSnackbar } = useSnackbar();
  const broadcastChannelMap = useContext(BroadcastChannelContext);
  const { fieldValuesCount } = caseForm;
  const fieldContainerRef = useRef<HTMLElement | null>(null);
  const activeSection = useMemo(() => formSections.find((fs) => fs.id === sectionId), [sectionId, formSections]);
  const { updateParam } = useSearchParamsWithUpdate();
  const dispatch = useAppDispatch();
  const lensId = useAppSelector(selectClientLens).id;

  useEffect(() => {
    if (sectionId !== undefined) {
      fieldContainerRef.current?.scrollTo({
        top: 0,
      });
    }
  }, [sectionId]);

  useEffect(() => {
    const pinChannel = broadcastChannelMap?.pin;
    if (pinChannel !== undefined) {
      pinChannel.onmessage = (event) => {
        if (event.data !== undefined) {
          const updatedCaseId = event.data;
          if ((updatedCaseId as number) === caseId) {
            enqueueSnackbar(
              "This profile has been updated from another tab. Please refresh the page before submitting any updates.",
              { variant: "info", persist: true }
            );
          }
        }
      };

      return () => {
        if (pinChannel?.onmessage !== null) {
          pinChannel.onmessage = null;
        }
      };
    }
  }, [broadcastChannelMap?.pin]);

  const scrollFieldValueGroupIntoView = (fieldId: number, valueGroupId: number): boolean => {
    if (fieldContainerRef.current) {
      const node = fieldContainerRef.current.querySelector(`#field-${fieldId}-response-${valueGroupId}`);
      if (node) {
        node.scrollIntoView();
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  };

  const scrollFieldIntoView = (fieldId: number) => {
    if (fieldContainerRef.current) {
      const node = fieldContainerRef.current.querySelector(`#form-field-${fieldId}`);
      if (node) {
        node.scrollIntoView();
      }
    }
  };

  const setLensAlertCallback = useContext(LensRefreshAlertContext);
  const caseFormRef = useRef(caseForm);
  useEffect(() => {
    caseFormRef.current = caseForm;
  }, [caseForm]);

  useEffect(() => {
    setLensAlertCallback &&
      setLensAlertCallback(() => ({ lens, fieldIds }) => {
        const callback = () =>
          dispatch(fetchForm({ lensId }))
            .unwrap()
            .then(() => caseFormRef.current.resetFieldsServer(fieldIds));
        enqueueSnackbar(`There's a new version of ${lens.name}. Update to continue working on this lens.`, {
          variant: "lensUpdate",
          preventDuplicate: true,
          persist: true,
          callback: callback,
        });
      });
    return () => {
      setLensAlertCallback && setLensAlertCallback(undefined);
    };
  }, [setLensAlertCallback]);

  useEffect(() => {
    const state = caseForm.focusFieldValueGroupState[0];
    const fieldId = state?.fieldId;
    const valueGroupId = state?.valueGroupId;
    if (fieldId !== undefined) {
      const field = formFields.find((f) => f.id === fieldId);
      if (field) {
        const section = formSections.find((s) => s.id === field.sectionId);
        if (section) {
          const formPageId = formPages.find((fp) => fp.id === section.formPageId)?.id;
          if (formPageId !== undefined) {
            updateParam("formPageId", formPageId.toString(), { replace: true });
          }
        }
        if (valueGroupId !== undefined) {
          if (!scrollFieldValueGroupIntoView(fieldId, valueGroupId)) {
            scrollFieldIntoView(fieldId);
          }
        } else {
          scrollFieldIntoView(fieldId);
        }
      }
    }
  }, [caseForm.focusFieldValueGroupState[0], fieldContainerRef.current]);

  if (!form || !caseForm.serverFieldValues || fieldValuesCount < form.fields.length) {
    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          height: "100%",
          alignItems: "center",
          justifyContent: "center",
        }}>
        <LoadingPattern />
      </Box>
    );
  }

  return (
    <Flex
      sx={{
        height: "100%",
        width: "100%",
        flexDirection: "row",
        alignItems: "flex-start",
      }}>
      <SBRCaseOutline
        formId={form.id}
        activeSectionId={sectionId}
        caseInfo={caseInfo}
        caseId={caseId}
        fieldContainerRef={fieldContainerRef}
        formValues={caseForm.serverFieldValues}
      />
      <Flex
        sx={{ maxHeight: "100%" }}
        flex="1"
        paddingLeft={2}
        paddingRight={2}
        minWidth={0}
        style={{ overflowX: "hidden" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", minHeight: 48, alignItems: "center" }}>
          <Breadcrumbs
            sx={{
              textTransform: "uppercase",
              fontSize: "10px",
              " .MuiBreadcrumbs-separator": {
                height: 10,
                lineHeight: "10px",
              },
            }}>
            <Link to={`?formPageId=${formPageId}&sectionId=`}>
              <P variant="body5" sx={{ ...SimpleHoverLinkSX, color: "grey.900", lineHeight: "10px" }}>
                All Sections
              </P>
            </Link>
            {activeSection !== undefined && (
              <P variant="body5" sx={{ color: "grey.400", lineHeight: "10px" }}>
                {activeSection.name}
              </P>
            )}
          </Breadcrumbs>
        </Box>

        <Box className="flex flex-col w-full h-full gap-4 pb-4 min-h-0" ref={fieldContainerRef}>
          {formSections
            .filter((s) => s.formPageId === formPageId)
            .map((section) => {
              const sectionValue = sectionValues.find((sv) => sv.sectionId === section.id);
              const sectionDisabled = sectionValue?.outcomeStatus === OutcomeStatus.NotApplicable;
              return (
                <Fragment key={section.id}>
                  {((activeSection !== undefined && section.id === sectionId) || activeSection === undefined) && (
                    <HeaderCard
                      title={
                        <HeaderCardTitle className="py-4">
                          <SectionTitle
                            sectionValue={sectionValue}
                            section={section}
                            formPageId={formPageId}
                            caseId={caseId}
                            formId={form.id}
                          />
                        </HeaderCardTitle>
                      }
                      className={activeSection !== undefined ? "min-h-0 h-full" : ""}
                      contentClassName={activeSection !== undefined ? "h-full min-h-0 flex flex-col" : ""}>
                      <div className="flex flex-col h-full min-h-0">
                        <SectionSummary
                          section={section}
                          sectionValue={sectionValue}
                          caseId={caseId}
                          formId={form.id}
                        />
                        <div
                          className={cn(
                            "flex flex-col p-4 pt-0 gap-4",
                            activeSection !== undefined ? "overflow-y-auto h-full min-h-0" : "" // full page scroll when viewing all sections
                          )}>
                          <div className="pt-4">
                            <SectionCaseDisplayGroups section={section} />
                          </div>
                          {sectionDisabled ? (
                            <Alert severity="info">Fields are hidden because the section is Not Applicable</Alert>
                          ) : (
                            formFields
                              .filter((f) =>
                                sectionId !== undefined ? f.sectionId === sectionId : f.sectionId === section.id
                              )
                              .filter((f) => caseForm.stateHandle[f.id] !== undefined)
                              .map((field) => (
                                <FormFieldProvider key={field.id} formField={field} form={form}>
                                  <SBRMemoFormFieldEditor hideLightbulb={false} />
                                </FormFieldProvider>
                              ))
                          )}
                        </div>
                      </div>
                    </HeaderCard>
                  )}
                </Fragment>
              );
            })}
        </Box>
      </Flex>
    </Flex>
  );
};

export default SBRCaseForm;
